/**
 * ContentEditorTools — RIGHT_INSP tools for content-focused scene types.
 *
 * Phase 13 P5 (June 2026)
 *
 * Covers scene types that route to GenericSceneEditorPage and need
 * structured content editing beyond the base MetadataForm:
 *
 *   * `prompt_canvas`  — steps, topic_summary, key_insight
 *   * `ai_lab`         — scenario/challenge_brief, starter_prompt,
 *                         steps_and_instructions, editable_fields
 *
 * Each tool binds to its scene type via `requiresSceneType` and
 * mutates through `setDomainMetadata` so undo/redo tracks edits.
 */
import React, { useCallback } from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { any, requiresSceneType, requiresCapability } from '../predicates';

// ────────────────────────────────────────────────────────────────────
// PromptCanvasEditor
// ────────────────────────────────────────────────────────────────────

function PromptCanvasEditor() {
  const { scene, setDomainMetadata } = useDesigner();

  const meta = scene?.domain_metadata || {};
  const steps = meta.steps || [];
  const topicSummary = meta.topic_summary || '';
  const keyInsight = meta.key_insight || '';
  const difficulty = meta.difficulty || '';

  const updateStep = useCallback((idx, patch) => {
    const next = steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setDomainMetadata({ steps: next });
  }, [steps, setDomainMetadata]);

  const addStep = useCallback(() => {
    setDomainMetadata({
      steps: [...steps, { title: '', prompt: '', expected_output: '' }],
    });
  }, [steps, setDomainMetadata]);

  const removeStep = useCallback((idx) => {
    setDomainMetadata({ steps: steps.filter((_, i) => i !== idx) });
  }, [steps, setDomainMetadata]);

  if (!scene) return null;

  return (
    <div className="designer-tool" data-tool-id="platform.prompt_canvas_editor">
      <h3 className="designer-tool__heading">Prompt Canvas</h3>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Topic summary</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={3}
          value={topicSummary}
          aria-label="Topic summary"
          placeholder="Brief overview of the topic…"
          onChange={(e) => setDomainMetadata({ topic_summary: e.target.value })}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Key insight</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={2}
          value={keyInsight}
          aria-label="Key insight"
          placeholder="Main takeaway for the learner…"
          onChange={(e) => setDomainMetadata({ key_insight: e.target.value })}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Difficulty</label>
        <select
          className="designer-tool__input"
          value={difficulty}
          aria-label="Difficulty level"
          onChange={(e) => setDomainMetadata({ difficulty: e.target.value })}
        >
          <option value="">— select —</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      <h4 className="designer-tool__subheading" style={{ marginTop: '1rem' }}>
        Steps ({steps.length})
      </h4>

      {steps.map((step, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid #2d3748',
            borderRadius: '6px',
            padding: '0.5rem',
            marginBottom: '0.5rem',
            background: '#0f1117',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Step {idx + 1}</span>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
              onClick={() => removeStep(idx)}
              aria-label={`Remove step ${idx + 1}`}
            >&times;</button>
          </div>
          <div className="designer-tool__row">
            <input
              type="text"
              className="designer-tool__input"
              value={step.title || ''}
              placeholder="Step title"
              aria-label={`Step ${idx + 1} title`}
              onChange={(e) => updateStep(idx, { title: e.target.value })}
            />
          </div>
          <div className="designer-tool__row">
            <textarea
              className="designer-tool__input designer-tool__input--textarea"
              rows={3}
              value={step.prompt || ''}
              placeholder="Prompt text…"
              aria-label={`Step ${idx + 1} prompt`}
              onChange={(e) => updateStep(idx, { prompt: e.target.value })}
            />
          </div>
          <div className="designer-tool__row">
            <textarea
              className="designer-tool__input designer-tool__input--textarea"
              rows={2}
              value={step.expected_output || ''}
              placeholder="Expected output (optional)…"
              aria-label={`Step ${idx + 1} expected output`}
              onChange={(e) => updateStep(idx, { expected_output: e.target.value })}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        className="designer-tool__btn"
        onClick={addStep}
        style={{
          width: '100%',
          padding: '0.4rem',
          borderRadius: '5px',
          border: '1px solid #2d3748',
          background: '#1e2130',
          color: '#e2e8f0',
          cursor: 'pointer',
          fontSize: '0.8125rem',
        }}
      >
        + Add step
      </button>
    </div>
  );
}

export const promptCanvasEditor = new DesignerTool({
  id:         'platform.prompt_canvas_editor',
  label:      'Prompt Canvas editor',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: any(
    requiresSceneType('prompt_canvas'),
    requiresCapability('free_text_reference'),
  ),
  Component:  PromptCanvasEditor,
  weight:     65,
  helpText:   'Edit prompt canvas steps, topic summary, and key insight.',
});

// ────────────────────────────────────────────────────────────────────
// AiLabEditor
// ────────────────────────────────────────────────────────────────────

function AiLabEditor() {
  const { scene, setDomainMetadata } = useDesigner();

  const meta = scene?.domain_metadata || {};
  const scenario = meta.scenario || meta.challenge_brief || '';
  const starterPrompt = meta.starter_prompt || meta.starter_code || '';
  const stepsText = meta.steps_and_instructions || '';
  const editableFields = meta.editable_fields || [];

  const updateEditableField = useCallback((idx, patch) => {
    const next = editableFields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    setDomainMetadata({ editable_fields: next });
  }, [editableFields, setDomainMetadata]);

  const addEditableField = useCallback(() => {
    setDomainMetadata({
      editable_fields: [...editableFields, { label: '', placeholder: '', type: 'text' }],
    });
  }, [editableFields, setDomainMetadata]);

  const removeEditableField = useCallback((idx) => {
    setDomainMetadata({ editable_fields: editableFields.filter((_, i) => i !== idx) });
  }, [editableFields, setDomainMetadata]);

  if (!scene) return null;

  return (
    <div className="designer-tool" data-tool-id="platform.ai_lab_editor">
      <h3 className="designer-tool__heading">AI Lab</h3>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Scenario / Challenge brief</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={4}
          value={scenario}
          aria-label="Scenario"
          placeholder="Describe the challenge the learner will work on…"
          onChange={(e) => setDomainMetadata({ scenario: e.target.value, challenge_brief: e.target.value })}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Starter prompt / code</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={4}
          value={starterPrompt}
          aria-label="Starter prompt"
          placeholder="Pre-filled prompt or starter code for the learner…"
          onChange={(e) => setDomainMetadata({ starter_prompt: e.target.value })}
          style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
        />
      </div>

      <div className="designer-tool__row">
        <label className="designer-tool__label">Steps &amp; instructions</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={5}
          value={stepsText}
          aria-label="Steps and instructions"
          placeholder="Step-by-step instructions for the learner…"
          onChange={(e) => setDomainMetadata({ steps_and_instructions: e.target.value })}
        />
      </div>

      <h4 className="designer-tool__subheading" style={{ marginTop: '1rem' }}>
        Editable fields ({editableFields.length})
      </h4>

      {editableFields.map((field, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid #2d3748',
            borderRadius: '6px',
            padding: '0.5rem',
            marginBottom: '0.5rem',
            background: '#0f1117',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Field {idx + 1}</span>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
              onClick={() => removeEditableField(idx)}
              aria-label={`Remove field ${idx + 1}`}
            >&times;</button>
          </div>
          <div className="designer-tool__row">
            <input
              type="text"
              className="designer-tool__input"
              value={field.label || ''}
              placeholder="Label"
              aria-label={`Field ${idx + 1} label`}
              onChange={(e) => updateEditableField(idx, { label: e.target.value })}
            />
          </div>
          <div className="designer-tool__row">
            <input
              type="text"
              className="designer-tool__input"
              value={field.placeholder || ''}
              placeholder="Placeholder text"
              aria-label={`Field ${idx + 1} placeholder`}
              onChange={(e) => updateEditableField(idx, { placeholder: e.target.value })}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        className="designer-tool__btn"
        onClick={addEditableField}
        style={{
          width: '100%',
          padding: '0.4rem',
          borderRadius: '5px',
          border: '1px solid #2d3748',
          background: '#1e2130',
          color: '#e2e8f0',
          cursor: 'pointer',
          fontSize: '0.8125rem',
        }}
      >
        + Add editable field
      </button>
    </div>
  );
}

export const aiLabEditor = new DesignerTool({
  id:         'platform.ai_lab_editor',
  label:      'AI Lab editor',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresSceneType('ai_lab'),
  Component:  AiLabEditor,
  weight:     65,
  helpText:   'Edit AI Lab scenario, starter prompt, steps, and editable fields.',
});

export const contentEditorTools = [promptCanvasEditor, aiLabEditor];
export default contentEditorTools;
